/**
 * Canadian federal + provincial income tax calculator (2025 & 2026).
 */

import type { ProvinceCode, ProvinceInfo, TaxBracket, TaxInput, TaxResult } from '../shared/types';

export const PROVINCES: Record<ProvinceCode, ProvinceInfo> = {
  ON: { name: 'Ontario', code: 'ON' },
  BC: { name: 'British Columbia', code: 'BC' },
  AB: { name: 'Alberta', code: 'AB' },
};

interface ProvinceTaxData {
  brackets: TaxBracket[];
  basicPersonalAmount: number;
}

interface YearTaxData {
  federal: {
    brackets: TaxBracket[];
    basicPersonalAmount: number;
  };
  provinces: Record<ProvinceCode, ProvinceTaxData>;
}

const TAX_DATA: Record<number, YearTaxData> = {
  2025: {
    federal: {
      brackets: [
        { upTo: 55867, rate: 0.15 },
        { upTo: 111733, rate: 0.205 },
        { upTo: 173205, rate: 0.26 },
        { upTo: 246752, rate: 0.29 },
        { upTo: Infinity, rate: 0.33 },
      ],
      basicPersonalAmount: 15705,
    },
    provinces: {
      ON: {
        brackets: [
          { upTo: 51891, rate: 0.0505 },
          { upTo: 103785, rate: 0.0915 },
          { upTo: 150000, rate: 0.1116 },
          { upTo: 220000, rate: 0.1216 },
          { upTo: Infinity, rate: 0.1316 },
        ],
        basicPersonalAmount: 12497,
      },
      BC: {
        brackets: [
          { upTo: 47937, rate: 0.0506 },
          { upTo: 95875, rate: 0.0762 },
          { upTo: 110076, rate: 0.1012 },
          { upTo: 133664, rate: 0.1229 },
          { upTo: 181232, rate: 0.147 },
          { upTo: 252752, rate: 0.168 },
          { upTo: Infinity, rate: 0.205 },
        ],
        basicPersonalAmount: 12932,
      },
      AB: {
        brackets: [
          { upTo: 148269, rate: 0.10 },
          { upTo: 177922, rate: 0.12 },
          { upTo: 237230, rate: 0.13 },
          { upTo: 355845, rate: 0.14 },
          { upTo: Infinity, rate: 0.15 },
        ],
        basicPersonalAmount: 22323,
      },
    },
  },
  2026: {
    federal: {
      brackets: [
        { upTo: 57375, rate: 0.15 },
        { upTo: 114750, rate: 0.205 },
        { upTo: 177882, rate: 0.26 },
        { upTo: 253414, rate: 0.29 },
        { upTo: Infinity, rate: 0.33 },
      ],
      basicPersonalAmount: 16120,
    },
    provinces: {
      ON: {
        brackets: [
          { upTo: 53227, rate: 0.0505 },
          { upTo: 106454, rate: 0.0915 },
          { upTo: 153900, rate: 0.1116 },
          { upTo: 225300, rate: 0.1216 },
          { upTo: Infinity, rate: 0.1316 },
        ],
        basicPersonalAmount: 12824,
      },
      BC: {
        brackets: [
          { upTo: 49179, rate: 0.0506 },
          { upTo: 98359, rate: 0.0762 },
          { upTo: 112948, rate: 0.1012 },
          { upTo: 137192, rate: 0.1229 },
          { upTo: 186037, rate: 0.147 },
          { upTo: 259316, rate: 0.168 },
          { upTo: Infinity, rate: 0.205 },
        ],
        basicPersonalAmount: 13281,
      },
      AB: {
        brackets: [
          { upTo: 152200, rate: 0.10 },
          { upTo: 182600, rate: 0.12 },
          { upTo: 243400, rate: 0.13 },
          { upTo: 365100, rate: 0.14 },
          { upTo: Infinity, rate: 0.15 },
        ],
        basicPersonalAmount: 22926,
      },
    },
  },
};

function calcBracketTax(income: number, brackets: TaxBracket[]): number {
  let tax = 0;
  let previous = 0;

  for (const bracket of brackets) {
    const taxableInBracket = Math.min(income, bracket.upTo) - previous;
    if (taxableInBracket <= 0) break;
    tax += taxableInBracket * bracket.rate;
    previous = bracket.upTo;
  }

  return tax;
}

function calcMarginalRate(income: number, brackets: TaxBracket[]): number {
  for (const bracket of brackets) {
    if (income <= bracket.upTo) return bracket.rate;
  }
  const lastBracket = brackets[brackets.length - 1];
  return lastBracket?.rate ?? 0;
}

function applyBasicPersonalCredit(grossTax: number, bpa: number, lowestRate: number): number {
  return Math.max(0, grossTax - bpa * lowestRate);
}

export function calculateCanadianTax({
  province,
  employmentIncome,
  otherIncome = 0,
  rrspDeductions = 0,
  capitalGains = 0,
  taxYear = 2026,
}: TaxInput): TaxResult {
  const yearData = TAX_DATA[taxYear];
  if (!yearData) {
    return { error: 'Tax data is only available for 2025 and 2026.' };
  }

  const provData = yearData.provinces[province as ProvinceCode];
  if (!provData) {
    return { error: `Province ${province} is not supported.` };
  }

  const grossIncome = Math.max(0, employmentIncome) + Math.max(0, otherIncome);
  const taxableCapitalGains = Math.max(0, capitalGains) * 0.5;
  const deductions = Math.max(0, rrspDeductions);
  const taxableIncome = Math.max(0, grossIncome + taxableCapitalGains - deductions);

  const federalGross = calcBracketTax(taxableIncome, yearData.federal.brackets);
  const federalLowest = yearData.federal.brackets[0]?.rate ?? 0;
  const federalTax = applyBasicPersonalCredit(
    federalGross,
    yearData.federal.basicPersonalAmount,
    federalLowest,
  );

  const provincialGross = calcBracketTax(taxableIncome, provData.brackets);
  const provincialLowest = provData.brackets[0]?.rate ?? 0;
  const provincialTax = applyBasicPersonalCredit(
    provincialGross,
    provData.basicPersonalAmount,
    provincialLowest,
  );

  const totalTax = federalTax + provincialTax;
  const afterTaxIncome = grossIncome + Math.max(0, capitalGains) - deductions - totalTax;
  const averageRate = grossIncome > 0 ? totalTax / grossIncome : 0;
  const marginalFederal = calcMarginalRate(taxableIncome, yearData.federal.brackets);
  const marginalProvincial = calcMarginalRate(taxableIncome, provData.brackets);
  const marginalRate = marginalFederal + marginalProvincial;

  return {
    province,
    taxYear,
    employmentIncome: Math.max(0, employmentIncome),
    otherIncome: Math.max(0, otherIncome),
    rrspDeductions: deductions,
    capitalGains: Math.max(0, capitalGains),
    grossIncome,
    taxableIncome,
    federalTax,
    provincialTax,
    totalTax,
    marginalRate,
    averageRate,
    afterTaxIncome,
  };
}
